import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft, FileText, Send, Download, CreditCard, CheckCircle,
  Clock, AlertCircle, User, Home, Calendar, DollarSign, Plus,
  Trash2, Edit2, Edit3, ExternalLink, Printer, Mail, Copy, X, Loader, Phone, MessageSquare, Percent
} from 'lucide-react';
import { invoiceStatuses, formatCurrency, isInvoiceOverdue, getPaymentTermsLabel, getDaysOverdue } from '../utils/invoiceData';
import { createStripeCustomer, getStripeInvoice } from '../utils/stripeApi';
import { openInvoiceForPrint } from '../utils/invoicePdf';
import { sendSMS } from '../utils/notificationsApi';
import './InvoiceDetail.css';

function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, updateInvoice, updateJob, deleteInvoice, updateClient, getClientById, getJobById, logClientCommunication } = useApp();
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showAddLineItem, setShowAddLineItem] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showLateFeeModal, setShowLateFeeModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' or 'text'
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    tipAmount: 0,
    method: 'card',
    date: format(new Date(), 'yyyy-MM-dd')
  });
  const [newLineItem, setNewLineItem] = useState({
    description: '',
    quantity: 1,
    unitPrice: 0
  });
  const [discountData, setDiscountData] = useState({
    type: 'percent', // 'percent' or 'fixed'
    value: 10,
    reason: ''
  });
  const [lateFeeData, setLateFeeData] = useState({
    type: 'fixed', // 'percent' or 'fixed'
    value: 25,
    sendUpdatedInvoice: true
  });
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [checkingStripe, setCheckingStripe] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [editingTipSplit, setEditingTipSplit] = useState(false);
  const [editTipSplit, setEditTipSplit] = useState([]);
  
  // Edit form state
  const [editData, setEditData] = useState({});
  const [editLineItems, setEditLineItems] = useState([]);
  
  // Deposit state
  const [depositData, setDepositData] = useState({
    type: 'percent',
    value: 50,
    dueDate: format(new Date(), 'yyyy-MM-dd')
  });

  // Find invoice
  const invoice = useMemo(() => {
    const inv = state.invoices.find(i => i.id === id);
    if (inv && (inv.status === 'pending' || inv.status === 'sent') && isInvoiceOverdue(inv)) {
      return { ...inv, displayStatus: 'overdue' };
    }
    return inv ? { ...inv, displayStatus: inv.status } : null;
  }, [state.invoices, id]);

  // Get related data
  const client = invoice ? getClientById(invoice.clientId) : null;
  const job = invoice?.jobId ? getJobById(invoice.jobId) : null;
  
  // Get team members for tip splitting
  const teamMembers = useMemo(() => {
    if (!job?.team) return [];
    return job.team.map(empId => {
      const emp = state.employees.find(e => e.id === empId);
      return emp ? {
        id: emp.id,
        name: `${emp.firstName} ${emp.lastName || ''}`.trim()
      } : null;
    }).filter(Boolean);
  }, [job, state.employees]);

  if (!invoice) {
    return (
      <div className="invoice-detail">
        <div className="not-found">
          <FileText size={48} />
          <h2>Invoice not found</h2>
          <Link to="/admin/invoices" className="btn-secondary">
            <ArrowLeft size={18} />
            Back to Invoices
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = invoiceStatuses[invoice.displayStatus] || invoiceStatuses.draft;

  // Handle send invoice via email or SMS — NO Stripe invoice created yet
  // Stripe invoice gets created in the portal when client selects tip and pays
  const handleSendInvoice = async () => {
    console.log('=== handleSendInvoice CALLED ===', { sendMethod });
    setSending(true);
    
    const isDepositInvoice = sendMethod === 'deposit';
    const sendViaEmail = sendMethod === 'email' || sendMethod === 'deposit';
    
    try {
      if (!client) {
        throw new Error('Client not found');
      }

      // Step 1: Ensure client has Stripe customer ID (needed later when creating Stripe invoice)
      let stripeCustomerId = client.stripeCustomerId;
      
      if (!stripeCustomerId) {
        const customerEmail = client.accountingEmail || client.email;
        if (!customerEmail) {
          throw new Error('Client needs an email address to create a Stripe account');
        }
        console.log('Creating Stripe customer for:', customerEmail);
        const customerResult = await createStripeCustomer({
          name: `${client.firstName} ${client.lastName}`,
          email: customerEmail,
          phone: client.phone,
          clientId: client.id
        });
        
        console.log('Stripe customer created:', customerResult);
        stripeCustomerId = customerResult.customerId;
        await updateClient(client.id, { stripeCustomerId });
      }

      const amountSent = isDepositInvoice ? invoice.depositAmount : invoice.total;
      // Generate payment token for direct pay link (no login required)
      const paymentToken = invoice.paymentToken || `pay_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      const portalLink = `${window.location.origin}/pay/${paymentToken}`;

      if (sendViaEmail) {
        if (!client.accountingEmail && !client.email) {
          throw new Error('Client has no email address');
        }

        const invoiceLogId = `log_${Date.now()}`;
        // Send branded email with portal link
        const emailResponse = await fetch('/.netlify/functions/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'invoice_sent',
            data: {
              clientId: client.id,
              commLogId: invoiceLogId,
              clientEmail: client.accountingEmail || client.email,
              clientName: client.firstName,
              invoiceNumber: invoice.invoiceNumber,
              amount: formatCurrency(amountSent),
              dueDate: format(parseISO(isDepositInvoice ? invoice.depositDueDate : invoice.dueDate), 'MMMM d, yyyy'),
              portalLink,
              isDeposit: isDepositInvoice
            }
          })
        });

        if (!emailResponse.ok) {
          const errorBody = await emailResponse.json().catch(() => ({}));
          throw new Error(errorBody.details || errorBody.error || 'Failed to send invoice email via SendGrid');
        }

        // Log communication
        await logClientCommunication(client.id, {
          id: invoiceLogId,
          type: 'email',
          category: 'invoice',
          subject: isDepositInvoice
            ? `Deposit Invoice ${invoice.invoiceNumber} Sent`
            : `Invoice ${invoice.invoiceNumber} Sent`,
          recipient: client.accountingEmail || client.email,
          status: 'sent',
          details: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            amount: amountSent,
            isDeposit: isDepositInvoice
          }
        });
        
      } else {
        // Send via SMS
        if (!client.phone) {
          throw new Error('Client has no phone number');
        }
        
        const smsResult = await sendSMS('invoice', {
          phone: client.phone,
          clientName: client.firstName,
          invoiceNumber: invoice.invoiceNumber,
          amount: amountSent,
          paymentUrl: portalLink
        });

        if (!smsResult.success) {
          throw new Error(smsResult.error || 'Failed to send invoice SMS');
        }

        await logClientCommunication(client.id, {
          type: 'sms',
          category: 'invoice',
          subject: `Invoice ${invoice.invoiceNumber} Sent`,
          recipient: client.phone,
          status: 'sent',
          details: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber,
            amount: amountSent
          }
        });
      }

      // Update invoice status with payment token for direct pay link
      const updateData = {
        status: 'sent',
        sentAt: new Date().toISOString(),
        sentVia: sendViaEmail ? 'email' : 'text',
        paymentToken
      };
      
      if (isDepositInvoice) {
        updateData.depositInvoiceSent = true;
        updateData.depositInvoiceSentAt = new Date().toISOString();
      }
      
      await updateInvoice(invoice.id, updateData);
      
      console.log('Invoice sent successfully (no Stripe invoice yet — created on payment)');
      setShowSendModal(false);
      
      if (isDepositInvoice) {
        alert(`Deposit invoice for ${formatCurrency(invoice.depositAmount)} sent successfully! The remaining balance of ${formatCurrency(invoice.total - invoice.depositAmount)} will be due upon completion.`);
      } else {
        alert(`Invoice sent successfully via ${sendViaEmail ? 'email' : 'text message'}!`);
      }
      
    } catch (error) {
      console.error('Error sending invoice:', error);
      
      if (client?.id) {
        await logClientCommunication(client.id, {
          type: sendViaEmail ? 'email' : 'sms',
          category: 'invoice',
          subject: `Invoice ${invoice.invoiceNumber} Send Failed`,
          recipient: sendViaEmail ? (client.accountingEmail || client.email) : client.phone,
          status: 'failed',
          error: error.message,
          details: {
            invoiceId: invoice.id,
            invoiceNumber: invoice.invoiceNumber
          }
        });
      }
      
      alert(`Failed to send invoice: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  // Handle record payment
  const handleRecordPayment = async () => {
    const totalPayment = parseFloat(paymentData.amount) + parseFloat(paymentData.tipAmount || 0);
    
    // Calculate tip split if there's a tip
    let tipSplit = [];
    if (paymentData.tipAmount > 0 && teamMembers.length > 0) {
      const splitAmount = paymentData.tipAmount / teamMembers.length;
      tipSplit = teamMembers.map(member => ({
        employeeId: member.id,
        employeeName: member.name,
        amount: Math.round(splitAmount * 100) / 100
      }));
    }

    await updateInvoice(invoice.id, {
      status: 'paid',
      paidAt: paymentData.date,
      paymentMethod: paymentData.method,
      amountPaid: totalPayment,
      tipAmount: parseFloat(paymentData.tipAmount) || 0,
      tipSplit,
      balance: 0
    });
    
    setShowPaymentModal(false);
    alert('Payment recorded successfully!');
  };

  // Handle add line item
  const handleAddLineItem = async () => {
    const total = newLineItem.quantity * newLineItem.unitPrice;
    const updatedLineItems = [
      ...invoice.lineItems,
      {
        id: `li_${Date.now()}`,
        type: 'addon',
        description: newLineItem.description,
        quantity: newLineItem.quantity,
        unitPrice: newLineItem.unitPrice,
        total
      }
    ];
    
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const newTotal = newSubtotal + invoice.tax;
    
    await updateInvoice(invoice.id, {
      lineItems: updatedLineItems,
      subtotal: newSubtotal,
      total: newTotal,
      balance: newTotal - invoice.amountPaid
    });
    
    setShowAddLineItem(false);
    setNewLineItem({ description: '', quantity: 1, unitPrice: 0 });
  };

  // Handle remove line item
  const handleRemoveLineItem = async (lineItemId) => {
    if (!window.confirm('Remove this line item?')) return;
    
    const updatedLineItems = invoice.lineItems.filter(li => li.id !== lineItemId);
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const newTotal = newSubtotal + invoice.tax;
    
    await updateInvoice(invoice.id, {
      lineItems: updatedLineItems,
      subtotal: newSubtotal,
      total: newTotal,
      balance: newTotal - invoice.amountPaid
    });
  };

  // Handle add discount
  const handleAddDiscount = async () => {
    // Calculate discount amount
    const currentSubtotal = invoice.lineItems
      .filter(item => item.type !== 'discount')
      .reduce((sum, item) => sum + item.total, 0);
    
    const discountAmount = discountData.type === 'percent'
      ? Math.round(currentSubtotal * (discountData.value / 100) * 100) / 100
      : discountData.value;
    
    const discountDescription = discountData.reason 
      ? `Discount: ${discountData.reason}${discountData.type === 'percent' ? ` (${discountData.value}%)` : ''}`
      : `Discount${discountData.type === 'percent' ? ` (${discountData.value}%)` : ''}`;
    
    // Remove any existing discount line items first
    const lineItemsWithoutDiscount = invoice.lineItems.filter(item => item.type !== 'discount');
    
    const updatedLineItems = [
      ...lineItemsWithoutDiscount,
      {
        id: `li_${Date.now()}_discount`,
        type: 'discount',
        description: discountDescription,
        quantity: 1,
        unitPrice: -discountAmount,
        total: -discountAmount
      }
    ];
    
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const newTotal = Math.max(0, newSubtotal + invoice.tax); // Don't go below 0
    
    await updateInvoice(invoice.id, {
      lineItems: updatedLineItems,
      subtotal: newSubtotal,
      total: newTotal,
      balance: newTotal - invoice.amountPaid,
      discountApplied: true,
      discountType: discountData.type,
      discountValue: discountData.value,
      discountReason: discountData.reason
    });
    
    setShowDiscountModal(false);
    setDiscountData({ type: 'percent', value: 10, reason: '' });
  };

  // Handle remove discount
  const handleRemoveDiscount = async () => {
    if (!window.confirm('Remove the discount?')) return;
    
    const updatedLineItems = invoice.lineItems.filter(item => item.type !== 'discount');
    const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
    const newTotal = newSubtotal + invoice.tax;
    
    await updateInvoice(invoice.id, {
      lineItems: updatedLineItems,
      subtotal: newSubtotal,
      total: newTotal,
      balance: newTotal - invoice.amountPaid,
      discountApplied: false,
      discountType: null,
      discountValue: null,
      discountReason: null
    });
  };

  // Handle add late fee
  const handleAddLateFee = async () => {
    setSaving(true);
    try {
      const daysOverdue = getDaysOverdue(invoice);
      
      // Calculate late fee amount
      const lateFeeAmount = lateFeeData.type === 'percent'
        ? Math.round(invoice.total * (lateFeeData.value / 100) * 100) / 100
        : lateFeeData.value;
      
      const lateFeeDescription = `Late Fee (${daysOverdue} days overdue)`;
      
      // Check if late fee already exists and remove it
      const lineItemsWithoutLateFee = invoice.lineItems.filter(item => item.type !== 'late_fee');
      
      const updatedLineItems = [
        ...lineItemsWithoutLateFee,
        {
          id: `li_${Date.now()}_latefee`,
          type: 'late_fee',
          description: lateFeeDescription,
          quantity: 1,
          unitPrice: lateFeeAmount,
          total: lateFeeAmount
        }
      ];
      
      const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
      const newTotal = newSubtotal + (invoice.tax || 0);
      const newDueDate = format(new Date(), 'yyyy-MM-dd'); // Reset due date to today
      
      await updateInvoice(invoice.id, {
        lineItems: updatedLineItems,
        subtotal: newSubtotal,
        total: newTotal,
        balance: newTotal - invoice.amountPaid,
        dueDate: newDueDate,
        lateFeeApplied: true,
        lateFeeAmount: lateFeeAmount,
        lateFeeAddedAt: new Date().toISOString()
      });
      
      // If user wants to send updated invoice
      if (lateFeeData.sendUpdatedInvoice) {
        // Trigger the resend modal with the appropriate method
        setShowLateFeeModal(false);
        setShowResendModal(true);
      } else {
        setShowLateFeeModal(false);
      }
      
      setLateFeeData({ type: 'fixed', value: 25, sendUpdatedInvoice: true });
    } catch (error) {
      console.error('Error adding late fee:', error);
      alert('Failed to add late fee. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const handleStartEdit = () => {
    setEditData({
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      paymentTerms: invoice.paymentTerms,
      notes: invoice.notes || '',
      requestDeposit: invoice.requestDeposit || false,
      depositType: invoice.depositType || 'percent',
      depositValue: invoice.depositValue || 50,
      depositDueDate: invoice.depositDueDate || format(new Date(), 'yyyy-MM-dd')
    });
    setEditLineItems([...invoice.lineItems]);
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
    setEditLineItems([]);
  };

  // Save edits
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const subtotal = editLineItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const depositAmount = editData.requestDeposit 
        ? (editData.depositType === 'percent' 
            ? Math.round(subtotal * (editData.depositValue / 100) * 100) / 100
            : editData.depositValue)
        : 0;

      await updateInvoice(invoice.id, {
        invoiceDate: editData.invoiceDate,
        dueDate: editData.dueDate,
        paymentTerms: editData.paymentTerms,
        notes: editData.notes,
        lineItems: editLineItems,
        subtotal,
        total: subtotal,
        balance: subtotal - (invoice.amountPaid || 0),
        requestDeposit: editData.requestDeposit,
        depositType: editData.requestDeposit ? editData.depositType : null,
        depositValue: editData.requestDeposit ? editData.depositValue : null,
        depositAmount: depositAmount,
        depositDueDate: editData.requestDeposit ? editData.depositDueDate : null
      });
      
      setIsEditing(false);
      setEditData({});
      setEditLineItems([]);
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Update line item in edit mode
  const updateEditLineItem = (id, field, value) => {
    setEditLineItems(editLineItems.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        updated.total = (updated.quantity || 0) * (updated.unitPrice || 0);
      }
      return updated;
    }));
  };

  // Add line item in edit mode
  const addEditLineItem = () => {
    setEditLineItems([...editLineItems, {
      id: `li_${Date.now()}`,
      type: 'service',
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    }]);
  };

  // Remove line item in edit mode
  const removeEditLineItem = (id) => {
    setEditLineItems(editLineItems.filter(item => item.id !== id));
  };

  // Record deposit payment
  const handleRecordDeposit = async () => {
    const depositAmt = invoice.depositAmount || 0;
    if (depositAmt <= 0) {
      alert('No deposit amount set for this invoice');
      return;
    }
    
    await updateInvoice(invoice.id, {
      depositPaid: true,
      depositPaidAt: new Date().toISOString(),
      depositPaidMethod: paymentData.method,
      amountPaid: (invoice.amountPaid || 0) + depositAmt,
      balance: invoice.total - (invoice.amountPaid || 0) - depositAmt
    });
    
    setShowDepositModal(false);
    alert(`Deposit of ${formatCurrency(depositAmt)} recorded!`);
  };

  // Copy shareable payment link (no login required)
  const copyPaymentLink = async () => {
    let token = invoice.paymentToken;
    if (!token) {
      token = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      await updateInvoice(invoice.id, { paymentToken: token });
    }
    const payLink = `${window.location.origin}/pay/${token}`;
    navigator.clipboard.writeText(payLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div className="invoice-detail">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/invoices')}>
            <ArrowLeft size={20} />
          </button>
          <div className="header-info">
            <h1>{invoice.invoiceNumber}</h1>
            <span 
              className="status-badge large"
              style={{ background: statusConfig.bg, color: statusConfig.color }}
            >
              {invoice.displayStatus === 'paid' && <CheckCircle size={16} />}
              {invoice.displayStatus === 'pending' && <Clock size={16} />}
              {invoice.displayStatus === 'overdue' && <AlertCircle size={16} />}
              {invoice.displayStatus === 'draft' && <FileText size={16} />}
              {statusConfig.label}
            </span>
          </div>
        </div>
        
        <div className="header-actions">
          {/* Debug: show invoice status */}
          {/* Status: {invoice.status} */}
          
          {/* Edit mode buttons */}
          {isEditing ? (
            <>
              <button className="btn-secondary" onClick={handleCancelEdit} disabled={saving}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              {/* Always show Edit for draft and overdue */}
              {(invoice.status === 'draft' || invoice.displayStatus === 'overdue') && (
                <button className="btn-secondary" onClick={handleStartEdit}>
                  <Edit2 size={18} />
                  Edit
                </button>
              )}
              
              {/* Always show Add Discount for draft/overdue without existing discount */}
              {(invoice.status === 'draft' || invoice.displayStatus === 'overdue') && !invoice.lineItems?.some(item => item.type === 'discount') && (
                <button className="btn-secondary" onClick={() => setShowDiscountModal(true)}>
                  <Percent size={18} />
                  Add Discount
                </button>
              )}
              
              {invoice.status === 'draft' && (
                <button className="btn-primary" onClick={() => setShowSendModal(true)}>
                  <Send size={18} />
                  Send Invoice
                </button>
              )}
              
              {/* Send Reminder - available for sent, pending, and overdue invoices */}
              {(invoice.status === 'sent' || invoice.status === 'pending' || invoice.displayStatus === 'overdue') && (
                <button className="btn-secondary" onClick={() => setShowResendModal(true)}>
                  <Send size={18} />
                  Send Reminder
                </button>
              )}

              {(invoice.status === 'sent' || invoice.status === 'pending' || invoice.displayStatus === 'overdue') && (
                <button className="btn-secondary" onClick={copyPaymentLink}>
                  {linkCopied ? <CheckCircle size={18} /> : <Copy size={18} />}
                  {linkCopied ? 'Copied!' : 'Copy Pay Link'}
                </button>
              )}

              {/* Check Stripe Status - for unpaid invoices with a Stripe invoice */}
              {(invoice.status === 'sent' || invoice.status === 'pending' || invoice.displayStatus === 'overdue') && invoice.stripeInvoiceId && (
                <button
                  className="btn-secondary"
                  disabled={checkingStripe}
                  onClick={async () => {
                    setCheckingStripe(true);
                    try {
                      const result = await getStripeInvoice(invoice.stripeInvoiceId);
                      const stripeInvoice = result.invoice;
                      if (stripeInvoice.status === 'paid') {
                        // Extract tip from line items
                        let tipAmount = 0;
                        const lines = stripeInvoice.lines?.data || [];
                        for (const line of lines) {
                          if ((line.description || '').toLowerCase().includes('tip')) {
                            tipAmount = (line.amount || 0) / 100;
                          }
                        }
                        const amountPaid = stripeInvoice.amount_paid / 100;
                        const stripePaidAt = stripeInvoice.status_transitions?.paid_at
                          ? new Date(stripeInvoice.status_transitions.paid_at * 1000).toISOString()
                          : null;
                        const paidAt = stripePaidAt || new Date().toISOString();

                        await updateInvoice(invoice.id, {
                          status: 'paid',
                          paidAt,
                          stripePaidAt: stripePaidAt || paidAt,
                          amountPaid,
                          tipAmount: tipAmount || invoice.tipAmount || 0,
                          balance: 0,
                          paymentMethod: 'stripe_invoice',
                          syncedFromManualCheck: true
                        });

                        // Update linked job
                        if (invoice.jobId) {
                          try {
                            await updateJob(invoice.jobId, {
                              invoicePaid: true,
                              invoicePaidAt: paidAt,
                              invoiceStripePaidAt: stripePaidAt || paidAt
                            });
                          } catch (e) {
                            console.error('Failed to update job:', e);
                          }
                        }
                        alert(`Invoice synced! Stripe confirms payment of ${formatCurrency(amountPaid)}.`);
                      } else {
                        alert(`Stripe status: "${stripeInvoice.status}" — not yet paid.`);
                      }
                    } catch (err) {
                      console.error('Stripe check failed:', err);
                      alert('Failed to check Stripe. Please try again.');
                    } finally {
                      setCheckingStripe(false);
                    }
                  }}
                >
                  {checkingStripe ? <Loader size={18} className="spin" /> : <CreditCard size={18} />}
                  {checkingStripe ? 'Checking...' : 'Check Stripe'}
                </button>
              )}

              {(invoice.status === 'sent' || invoice.status === 'pending' || invoice.displayStatus === 'overdue') && (
                <>
                  {/* Late Fee button - shows after 2 days overdue and if no late fee already applied */}
                  {invoice.displayStatus === 'overdue' && getDaysOverdue(invoice) >= 2 && !invoice.lateFeeApplied && (
                    <button className="btn-warning" onClick={() => setShowLateFeeModal(true)}>
                      <AlertCircle size={18} />
                      Add Late Fee
                    </button>
                  )}
                  {/* Deposit button if deposit requested but not paid */}
                  {invoice.requestDeposit && !invoice.depositPaid && (
                    <button className="btn-gold" onClick={() => setShowDepositModal(true)}>
                      <DollarSign size={18} />
                      Record Deposit ({formatCurrency(invoice.depositAmount)})
                    </button>
                  )}
                  <button className="btn-success" onClick={async () => {
                    if (window.confirm(`Mark invoice #${invoice.invoiceNumber} as PAID?\n\nAmount: ${formatCurrency(invoice.balance || invoice.total)}`)) {
                      try {
                        await updateInvoice(invoice.id, {
                          status: 'paid',
                          paidAt: new Date().toISOString(),
                          amountPaid: invoice.balance || invoice.total,
                          balance: 0,
                          paymentMethod: 'manual',
                          manuallyMarkedPaid: true,
                          manuallyMarkedPaidAt: new Date().toISOString()
                        });
                      } catch (err) {
                        console.error('Failed to mark as paid:', err);
                        alert('Failed to update invoice. Please try again.');
                      }
                    }
                  }}>
                    <CheckCircle size={18} />
                    Mark as Paid
                  </button>
                  <button className="btn-primary" onClick={() => {
                    setPaymentData({ ...paymentData, amount: invoice.balance });
                    setShowPaymentModal(true);
                  }}>
                    <CreditCard size={18} />
                    Record Payment
                  </button>
                </>
              )}
              
              <button
                className="btn-icon"
                title={invoice.stripeInvoiceId ? "Download PDF" : "Print Invoice"}
                disabled={downloadingPdf}
                onClick={async () => {
                  if (!invoice.stripeInvoiceId) {
                    openInvoiceForPrint(invoice);
                    return;
                  }
                  setDownloadingPdf(true);
                  try {
                    const result = await getStripeInvoice(invoice.stripeInvoiceId);
                    const pdfUrl = result.pdfUrl || result.invoice?.invoice_pdf;
                    if (pdfUrl) {
                      window.open(pdfUrl, '_blank');
                    } else {
                      alert('PDF is not available yet. The invoice must be finalized first.');
                    }
                  } catch (err) {
                    console.error('Failed to download PDF:', err);
                    alert('Failed to download invoice PDF. Please try again.');
                  } finally {
                    setDownloadingPdf(false);
                  }
                }}
              >
                {downloadingPdf ? <Loader size={18} className="spinner" /> : <Download size={18} />}
              </button>
              
              {/* Delete Button - only for draft or if admin wants to remove */}
              <button 
                className="btn-icon delete" 
                title="Delete Invoice"
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to delete invoice #${invoice.invoiceNumber}?\n\nThis action cannot be undone.`)) {
                    try {
                      await deleteInvoice(invoice.id);
                      navigate('/admin/invoices');
                    } catch (err) {
                      console.error('Delete failed:', err);
                      alert('Failed to delete invoice. Please try again.');
                    }
                  }
                }}
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-grid">
        {/* Invoice Details */}
        <div className="invoice-content">
          {/* Company Header */}
          <div className="invoice-company">
            <div className="company-logo">
              <span className="logo-icon">🌿</span>
              <div className="company-name">
                <span className="name">Two Sisters</span>
                <span className="tagline">Cleaning & Organizing</span>
              </div>
            </div>
            <div className="company-contact">
              <p>2114 Angus Rd</p>
              <p>Charlottesville, VA 22901</p>
              <p>(434) 207-2246</p>
            </div>
          </div>

          {/* Bill To & Invoice Info */}
          <div className="invoice-parties">
            <div className="bill-to">
              <h3>Bill To</h3>
              <p className="client-name">{invoice.clientName}</p>
              {invoice.propertyAddress && <p>{invoice.propertyAddress}</p>}
              {invoice.clientEmail && <p>{invoice.clientEmail}</p>}
              {invoice.clientPhone && <p>{invoice.clientPhone}</p>}
            </div>
            
            <div className="invoice-info">
              <div className="info-row">
                <span className="label">Invoice Number</span>
                <span className="value">{invoice.invoiceNumber}</span>
              </div>
              <div className="info-row">
                <span className="label">Invoice Date</span>
                <span className="value">{format(parseISO(invoice.invoiceDate), 'MMMM d, yyyy')}</span>
              </div>
              <div className="info-row">
                <span className="label">Due Date</span>
                <span className={`value ${invoice.displayStatus === 'overdue' ? 'overdue' : ''}`}>
                  {format(parseISO(invoice.dueDate), 'MMMM d, yyyy')}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Terms</span>
                <span className="value">{getPaymentTermsLabel(invoice.paymentTerms)}</span>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="line-items-section">
            <div className="section-header">
              <h3>Services</h3>
              <div className="section-actions">
                {isEditing && (
                  <button className="btn-text" onClick={addEditLineItem}>
                    <Plus size={16} />
                    Add Item
                  </button>
                )}
                {!isEditing && (invoice.status === 'draft' || invoice.displayStatus === 'overdue') && (
                  <>
                    <button className="btn-text" onClick={() => setShowAddLineItem(true)}>
                      <Plus size={16} />
                      Add Item
                    </button>
                    {!invoice.lineItems.some(item => item.type === 'discount') && (
                      <button className="btn-text discount-btn" onClick={() => setShowDiscountModal(true)}>
                        <Percent size={16} />
                        Add Discount
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <table className="line-items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="center">Qty</th>
                  <th className="right">Unit Price</th>
                  <th className="right">Amount</th>
                  {(isEditing || invoice.status === 'draft' || invoice.displayStatus === 'overdue') && <th></th>}
                </tr>
              </thead>
              <tbody>
                {isEditing ? (
                  // Edit mode - editable fields
                  editLineItems.map(item => (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateEditLineItem(item.id, 'description', e.target.value)}
                          placeholder="Description"
                          className="edit-input"
                        />
                      </td>
                      <td className="center">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateEditLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                          min="1"
                          className="edit-input small"
                        />
                      </td>
                      <td className="right">
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateEditLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                          step="0.01"
                          min="0"
                          className="edit-input small"
                        />
                      </td>
                      <td className="right">{formatCurrency(item.total || 0)}</td>
                      <td className="actions">
                        <button 
                          className="btn-icon-sm danger"
                          onClick={() => removeEditLineItem(item.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  // View mode
                  invoice.lineItems.map(item => (
                    <tr key={item.id} className={item.type === 'discount' ? 'discount-row' : ''}>
                      <td>{item.description}</td>
                      <td className="center">{item.type === 'discount' ? '-' : item.quantity}</td>
                      <td className="right">{item.type === 'discount' ? '-' : formatCurrency(item.unitPrice)}</td>
                      <td className={`right ${item.type === 'discount' ? 'discount-amount' : ''}`}>
                        {formatCurrency(item.total)}
                      </td>
                      {(invoice.status === 'draft' || invoice.displayStatus === 'overdue') && (
                        <td className="actions">
                          <button
                            className="btn-icon-sm"
                            onClick={() => item.type === 'discount' ? handleRemoveDiscount() : handleRemoveLineItem(item.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="invoice-totals">
            <div className="totals-row">
              <span>Subtotal</span>
              <span>{formatCurrency(isEditing ? editLineItems.reduce((sum, i) => sum + (i.total || 0), 0) : invoice.subtotal)}</span>
            </div>
            {invoice.tax > 0 && (
              <div className="totals-row">
                <span>Tax</span>
                <span>{formatCurrency(invoice.tax)}</span>
              </div>
            )}
            <div className="totals-row total">
              <span>Total</span>
              <span>{formatCurrency(isEditing ? editLineItems.reduce((sum, i) => sum + (i.total || 0), 0) : invoice.total)}</span>
            </div>
            
            {/* Deposit Section */}
            {isEditing ? (
              <div className="deposit-edit-section">
                <label className="deposit-toggle">
                  <input
                    type="checkbox"
                    checked={editData.requestDeposit || false}
                    onChange={(e) => setEditData({...editData, requestDeposit: e.target.checked})}
                  />
                  Request Deposit
                </label>
                {editData.requestDeposit && (
                  <div className="deposit-options">
                    <div className="deposit-type-row">
                      <select
                        value={editData.depositType}
                        onChange={(e) => setEditData({...editData, depositType: e.target.value})}
                      >
                        <option value="percent">Percentage</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                      <input
                        type="number"
                        value={editData.depositValue}
                        onChange={(e) => setEditData({...editData, depositValue: parseFloat(e.target.value) || 0})}
                        min="0"
                        max={editData.depositType === 'percent' ? 100 : undefined}
                      />
                      <span>{editData.depositType === 'percent' ? '%' : '$'}</span>
                    </div>
                    <div className="deposit-due-row">
                      <label>Deposit Due:</label>
                      <input
                        type="date"
                        value={editData.depositDueDate}
                        onChange={(e) => setEditData({...editData, depositDueDate: e.target.value})}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : invoice.requestDeposit && (
              <div className="deposit-info">
                <div className="totals-row deposit">
                  <span>Deposit Required</span>
                  <span>{formatCurrency(invoice.depositAmount)}</span>
                </div>
                {invoice.depositPaid ? (
                  <div className="totals-row deposit-paid">
                    <span>✓ Deposit Paid</span>
                    <span>{invoice.depositPaidAt ? format(parseISO(invoice.depositPaidAt), 'MMM d') : ''}</span>
                  </div>
                ) : (
                  <div className="totals-row deposit-due">
                    <span>Deposit Due</span>
                    <span>{invoice.depositDueDate ? format(parseISO(invoice.depositDueDate), 'MMM d, yyyy') : '-'}</span>
                  </div>
                )}
              </div>
            )}
            
            {invoice.tipAmount > 0 && (
              <div className="totals-row tip">
                <span>Tip</span>
                <span>{formatCurrency(invoice.tipAmount)}</span>
              </div>
            )}
            {invoice.amountPaid > 0 && (
              <div className="totals-row paid">
                <span>Amount Paid</span>
                <span>{formatCurrency(invoice.amountPaid)}</span>
              </div>
            )}
            {invoice.balance > 0 && (
              <div className="totals-row balance">
                <span>Balance Due</span>
                <span>{formatCurrency(invoice.balance)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="invoice-sidebar">
          {/* Payment Status */}
          <div className="sidebar-card">
            <h3>Payment Status</h3>
            {invoice.status === 'paid' ? (
              <div className="payment-status paid">
                <CheckCircle size={24} />
                <div className="status-details">
                  <span className="status-text">Paid in Full</span>
                  {invoice.stripePaidAt ? (
                    <span className="status-date">
                      Stripe paid: {format(parseISO(invoice.stripePaidAt), 'MMM d, yyyy')}
                    </span>
                  ) : invoice.paidAt && (
                    <span className="status-date">
                      {format(parseISO(invoice.paidAt), 'MMM d, yyyy')}
                    </span>
                  )}
                  {invoice.paymentMethod && (
                    <span className="status-method">via {invoice.paymentMethod}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className={`payment-status ${invoice.displayStatus}`}>
                {invoice.displayStatus === 'overdue' ? (
                  <AlertCircle size={24} />
                ) : (
                  <Clock size={24} />
                )}
                <div className="status-details">
                  <span className="status-text">
                    {invoice.displayStatus === 'overdue' ? 'Overdue' : 'Awaiting Payment'}
                  </span>
                  <span className="status-amount">{formatCurrency(invoice.balance)}</span>
                  {invoice.lastReminderSent && (
                    <span className="last-reminder-info">
                      Last reminder: {format(parseISO(invoice.lastReminderSent), 'MMM d, yyyy h:mm a')}
                      {invoice.reminderCount > 1 && ` (${invoice.reminderCount} total)`}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tip Split (if paid with tip) */}
          {invoice.tipAmount > 0 && (
            <div className="sidebar-card">
              <div className="sidebar-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Tip Split</h3>
                {!editingTipSplit ? (
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditTipSplit(invoice.tipSplit?.length > 0 
                        ? invoice.tipSplit.map(s => ({ ...s }))
                        : teamMembers.map(m => ({
                            employeeId: m.id,
                            employeeName: m.name,
                            amount: Math.round((invoice.tipAmount / teamMembers.length) * 100) / 100
                          }))
                      );
                      setEditingTipSplit(true);
                    }}
                  >
                    <Edit3 size={14} /> Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        await updateInvoice(invoice.id, { tipSplit: editTipSplit });
                        setEditingTipSplit(false);
                      }}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => setEditingTipSplit(false)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              {!editingTipSplit ? (
                <div className="tip-split-list">
                  {invoice.tipSplit?.length > 0 ? (
                    <>
                      {invoice.tipSplit.map((split, idx) => (
                        <div key={idx} className="tip-split-row">
                          <span className="employee-name">{split.employeeName}</span>
                          <span className="employee-amount">{formatCurrency(split.amount)}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: '8px 0' }}>
                      No team assigned to tip. Click Edit to assign.
                    </p>
                  )}
                  <div className="tip-split-total">
                    <span>Total Tip</span>
                    <span>{formatCurrency(invoice.tipAmount)}</span>
                  </div>
                </div>
              ) : (
                <div className="tip-split-edit">
                  {editTipSplit.map((split, idx) => (
                    <div key={idx} className="tip-split-edit-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ flex: 1, fontSize: '14px' }}>{split.employeeName}</span>
                      <span style={{ fontSize: '14px', color: '#64748b' }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={split.amount}
                        onChange={(e) => {
                          const updated = [...editTipSplit];
                          updated[idx].amount = parseFloat(e.target.value) || 0;
                          setEditTipSplit(updated);
                        }}
                        style={{ width: '80px', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                      />
                      <button
                        onClick={() => setEditTipSplit(editTipSplit.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <div style={{ marginTop: '8px' }}>
                    <select
                      onChange={(e) => {
                        if (!e.target.value) return;
                        const emp = state.employees.find(em => em.id === e.target.value);
                        if (emp && !editTipSplit.find(s => s.employeeId === emp.id)) {
                          setEditTipSplit([...editTipSplit, {
                            employeeId: emp.id,
                            employeeName: `${emp.firstName} ${emp.lastName || ''}`.trim(),
                            amount: 0
                          }]);
                        }
                        e.target.value = '';
                      }}
                      style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#64748b' }}
                    >
                      <option value="">+ Add employee...</option>
                      {state.employees
                        .filter(emp => emp.status === 'active' && !editTipSplit.find(s => s.employeeId === emp.id))
                        .map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.firstName} {emp.lastName || ''}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ marginTop: '8px', width: '100%' }}
                    onClick={() => {
                      if (editTipSplit.length === 0) return;
                      const evenSplit = Math.round((invoice.tipAmount / editTipSplit.length) * 100) / 100;
                      setEditTipSplit(editTipSplit.map(s => ({ ...s, amount: evenSplit })));
                    }}
                  >
                    Split Evenly ({editTipSplit.length > 0 ? formatCurrency(invoice.tipAmount / editTipSplit.length) : '$0'} each)
                  </button>
                  <div className="tip-split-total" style={{ marginTop: '10px' }}>
                    <span>Assigned</span>
                    <span style={{ color: Math.abs(editTipSplit.reduce((s, t) => s + t.amount, 0) - invoice.tipAmount) < 0.02 ? '#16a34a' : '#ef4444' }}>
                      {formatCurrency(editTipSplit.reduce((s, t) => s + t.amount, 0))} / {formatCurrency(invoice.tipAmount)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Related Job */}
          {job && (
            <div className="sidebar-card">
              <h3>Related Job</h3>
              <div className="related-job">
                <div className="job-info">
                  <Calendar size={16} />
                  <span>{format(parseISO(job.date), 'MMM d, yyyy')}</span>
                </div>
                <div className="job-info">
                  <Clock size={16} />
                  <span>{job.scheduledTime} - {job.scheduledEndTime}</span>
                </div>
                <div className="job-info">
                  <User size={16} />
                  <span>{job.teamNames?.join(', ') || 'Unassigned'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Client Info */}
          {client && (
            <div className="sidebar-card">
              <h3>Client</h3>
              <Link to={`/clients/${client.id}`} className="client-link">
                <User size={16} />
                <span>{client.firstName} {client.lastName}</span>
                <ExternalLink size={14} />
              </Link>
            </div>
          )}

          {/* Stripe Link */}
          {invoice.stripePaymentUrl && (
            <div className="sidebar-card">
              <h3>Payment Link</h3>
              <a 
                href={invoice.stripePaymentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="stripe-link"
              >
                <ExternalLink size={16} />
                Open in Stripe
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Send Invoice Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Invoice</h2>
              <button className="close-btn" onClick={() => setShowSendModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Invoice Type Selection - only show if deposit requested */}
              {invoice.requestDeposit && !invoice.depositPaid && (
                <div className="invoice-type-selection">
                  <label className="send-method-label">What to send:</label>
                  <div className="send-method-options">
                    <button 
                      className={`send-method-btn ${sendMethod !== 'deposit' ? 'active' : ''}`}
                      onClick={() => setSendMethod('email')}
                    >
                      <FileText size={18} />
                      Full Invoice
                      <span className="btn-amount">{formatCurrency(invoice.total)}</span>
                    </button>
                    <button 
                      className={`send-method-btn deposit-btn ${sendMethod === 'deposit' ? 'active' : ''}`}
                      onClick={() => setSendMethod('deposit')}
                    >
                      <DollarSign size={18} />
                      Deposit Only
                      <span className="btn-amount">{formatCurrency(invoice.depositAmount)}</span>
                    </button>
                  </div>
                  {sendMethod === 'deposit' && (
                    <p className="deposit-note-inline">
                      Client will receive a deposit invoice. Remaining {formatCurrency(invoice.total - invoice.depositAmount)} due upon completion.
                    </p>
                  )}
                </div>
              )}

              {/* Send Method Selection */}
              <div className="send-method-selection">
                <label className="send-method-label">Send via:</label>
                <div className="send-method-options">
                  <button 
                    className={`send-method-btn ${sendMethod === 'email' || sendMethod === 'deposit' ? 'active' : ''}`}
                    onClick={() => setSendMethod(invoice.requestDeposit && sendMethod === 'deposit' ? 'deposit' : 'email')}
                    disabled={!(client?.accountingEmail || client?.email)}
                  >
                    <Mail size={18} />
                    Email
                    {!(client?.accountingEmail || client?.email) && <span className="no-contact">(no email)</span>}
                  </button>
                  <button 
                    className={`send-method-btn ${sendMethod === 'text' ? 'active' : ''}`}
                    onClick={() => setSendMethod('text')}
                    disabled={!client?.phone}
                  >
                    <MessageSquare size={18} />
                    Text
                    {!client?.phone && <span className="no-contact">(no phone)</span>}
                  </button>
                </div>
              </div>

              <p className="send-description">
                {sendMethod === 'deposit' ? (
                  <>Deposit invoice for <strong>{formatCurrency(invoice.depositAmount)}</strong> will be sent to <strong>{client?.accountingEmail || client?.email}</strong>.</>
                ) : sendMethod === 'email' ? (
                  <>Invoice will be sent to <strong>{client?.accountingEmail || client?.email}</strong> via Stripe.</>
                ) : (
                  <>Payment link will be texted to <strong>{client?.phone}</strong>.</>
                )}
              </p>
              
              <div className="send-summary">
                <div className="summary-row">
                  <span>{sendMethod === 'deposit' ? 'Deposit Amount' : 'Invoice Total'}</span>
                  <span>{formatCurrency(sendMethod === 'deposit' ? invoice.depositAmount : invoice.total)}</span>
                </div>
                <div className="summary-row">
                  <span>Due Date</span>
                  <span>{format(parseISO(sendMethod === 'deposit' && invoice.depositDueDate ? invoice.depositDueDate : invoice.dueDate), 'MMM d, yyyy')}</span>
                </div>
                {sendMethod === 'deposit' && (
                  <div className="summary-row remaining">
                    <span>Remaining Balance</span>
                    <span>{formatCurrency(invoice.total - invoice.depositAmount)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSendInvoice}
                disabled={sending}
              >
                {sending ? 'Sending...' : sendMethod === 'deposit' ? 'Send Deposit Invoice' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend/Reminder Modal */}
      {showResendModal && (
        <div className="modal-overlay" onClick={() => setShowResendModal(false)}>
          <div className="modal-content resend-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Payment Reminder</h2>
              <button className="close-btn" onClick={() => setShowResendModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="reminder-options">
                <h4>Choose how to send:</h4>
                
                {/* Email Option */}
                <button 
                  className="reminder-option"
                  disabled={!(client?.accountingEmail || client?.email) || sending}
                  onClick={async () => {
                    setSending(true);
                    try {
                      const reminderLogId = `log_${Date.now()}`;
                      const reminderEmail = client.accountingEmail || client.email;
                      await fetch('/.netlify/functions/send-email', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          type: 'raw',
                          data: {
                            clientId: client?.id,
                            commLogId: reminderLogId,
                            to: reminderEmail,
                            subject: `Payment Reminder - Invoice #${invoice.invoiceNumber}`,
                            html: `
                              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                                <h2 style="color: #2c5f4e;">Payment Reminder</h2>
                                <p>Hi ${invoice.clientName},</p>
                                <p>This is a friendly reminder that your invoice for <strong>${formatCurrency(invoice.balance || invoice.total)}</strong> is ${invoice.displayStatus === 'overdue' ? 'past due' : 'due soon'}.</p>
                                <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
                                  <p style="margin: 0;"><strong>Invoice #${invoice.invoiceNumber}</strong></p>
                                  <p style="margin: 5px 0;">Amount Due: <strong>${formatCurrency(invoice.balance || invoice.total)}</strong></p>
                                  ${invoice.dueDate ? `<p style="margin: 5px 0;">Due Date: ${format(parseISO(invoice.dueDate), 'MMMM d, yyyy')}</p>` : ''}
                                </div>
                                <p><a href="${invoice.paymentToken ? `${window.location.origin}/pay/${invoice.paymentToken}` : `${window.location.origin}/portal?tab=invoices`}" style="display: inline-block; background: #2c5f4e; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: bold;">View Invoice & Pay</a></p>
                                <div style="background: #fef3c7; padding: 14px 18px; border-radius: 8px; margin: 16px 0; border-left: 4px solid #f59e0b;">
                                  <p style="margin: 0; font-size: 14px; color: #92400e;">💛 <strong>Love your clean?</strong> You can leave a tip for your cleaning team when you pay — they really appreciate it!</p>
                                </div>
                                <p>Thank you for your business!</p>
                                <p style="color: #64748b; font-size: 14px;">Two Sisters Cleaning & Organizing</p>
                              </div>
                            `
                          }
                        })
                      });

                      await updateInvoice(invoice.id, {
                        lastReminderSent: new Date().toISOString(),
                        reminderCount: (invoice.reminderCount || 0) + 1
                      });

                      if (client?.id) {
                        await logClientCommunication(client.id, {
                          id: reminderLogId,
                          type: 'email',
                          category: 'invoice_reminder',
                          subject: `Payment Reminder - Invoice #${invoice.invoiceNumber}`,
                          recipient: reminderEmail,
                          status: 'sent',
                          details: { invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber }
                        });
                      }

                      alert('Reminder email sent!');
                      setShowResendModal(false);
                    } catch (error) {
                      console.error('Error sending reminder:', error);
                      alert('Failed to send reminder. Please try again.');
                    } finally {
                      setSending(false);
                    }
                  }}
                >
                  <div className="option-icon email">
                    <Mail size={24} />
                  </div>
                  <div className="option-content">
                    <span className="option-title">Send via Email</span>
                    <span className="option-detail">
                      {client?.accountingEmail || client?.email || 'No email on file'}
                    </span>
                  </div>
                  {sending && <Loader size={18} className="spinner" />}
                </button>

                {/* Text Option */}
                <button 
                  className="reminder-option"
                  disabled={!client?.phone || sending}
                  onClick={async () => {
                    setSending(true);
                    try {
                      // Use the proper sendSMS format with type and data
                      await sendSMS('payment_reminder', {
                        phone: client.phone,
                        clientName: invoice.clientName,
                        invoiceNumber: invoice.invoiceNumber,
                        amount: invoice.balance || invoice.total,
                        paymentLink: invoice.paymentToken ? `${window.location.origin}/pay/${invoice.paymentToken}` : `${window.location.origin}/portal?tab=invoices`
                      });
                      
                      await updateInvoice(invoice.id, {
                        lastReminderSent: new Date().toISOString(),
                        reminderCount: (invoice.reminderCount || 0) + 1
                      });
                      
                      await logClientCommunication(client.id, {
                        type: 'sms',
                        category: 'invoice_reminder',
                        subject: `Payment Reminder - Invoice #${invoice.invoiceNumber}`,
                        recipient: client.phone,
                        status: 'sent',
                        details: { invoiceId: invoice.id, invoiceNumber: invoice.invoiceNumber }
                      });
                      
                      alert('Reminder text sent!');
                      setShowResendModal(false);
                    } catch (error) {
                      console.error('Error sending SMS:', error);
                      alert('Failed to send text. Please try again.');
                    } finally {
                      setSending(false);
                    }
                  }}
                >
                  <div className="option-icon text">
                    <MessageSquare size={24} />
                  </div>
                  <div className="option-content">
                    <span className="option-title">Send via Text</span>
                    <span className="option-detail">
                      {client?.phone || 'No phone on file'}
                    </span>
                  </div>
                  {sending && <Loader size={18} className="spinner" />}
                </button>

                {/* Copy Link Option */}
                <button
                  className="reminder-option"
                  onClick={async () => {
                    let token = invoice.paymentToken;
                    if (!token) {
                      token = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
                      await updateInvoice(invoice.id, { paymentToken: token });
                    }
                    const payLink = `${window.location.origin}/pay/${token}`;
                    navigator.clipboard.writeText(payLink);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                >
                  <div className="option-icon copy">
                    {linkCopied ? <CheckCircle size={24} /> : <Copy size={24} />}
                  </div>
                  <div className="option-content">
                    <span className="option-title">{linkCopied ? 'Link Copied!' : 'Copy Payment Link'}</span>
                    <span className="option-detail">Share link manually — no login required</span>
                  </div>
                </button>
              </div>

              {invoice.lastReminderSent && (
                <p className="last-reminder">
                  Last reminder sent: {format(parseISO(invoice.lastReminderSent), 'MMM d, yyyy h:mm a')}
                  {invoice.reminderCount > 1 && ` (${invoice.reminderCount} total)`}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowResendModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Record Payment</h2>
              <button className="close-btn" onClick={() => setShowPaymentModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              
              <div className="form-group">
                <label>Tip Amount (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.tipAmount}
                  onChange={(e) => setPaymentData({...paymentData, tipAmount: e.target.value})}
                  placeholder="0.00"
                />
                {teamMembers.length > 0 && paymentData.tipAmount > 0 && (
                  <span className="form-hint">
                    Split evenly: {formatCurrency(paymentData.tipAmount / teamMembers.length)} each to {teamMembers.map(m => m.name).join(', ')}
                  </span>
                )}
              </div>
              
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
                >
                  <option value="card">Credit/Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Payment Date</label>
                <input
                  type="date"
                  value={paymentData.date}
                  onChange={(e) => setPaymentData({...paymentData, date: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowPaymentModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleRecordPayment}>
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Line Item Modal */}
      {showAddLineItem && (
        <div className="modal-overlay" onClick={() => setShowAddLineItem(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Line Item</h2>
              <button className="close-btn" onClick={() => setShowAddLineItem(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={newLineItem.description}
                  onChange={(e) => setNewLineItem({...newLineItem, description: e.target.value})}
                  placeholder="e.g., Inside Oven Cleaning"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    min="1"
                    value={newLineItem.quantity}
                    onChange={(e) => setNewLineItem({...newLineItem, quantity: parseInt(e.target.value) || 1})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Unit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newLineItem.unitPrice}
                    onChange={(e) => setNewLineItem({...newLineItem, unitPrice: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="line-item-total">
                <span>Total:</span>
                <span>{formatCurrency(newLineItem.quantity * newLineItem.unitPrice)}</span>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAddLineItem(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddLineItem}
                disabled={!newLineItem.description || newLineItem.unitPrice <= 0}
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Deposit Modal */}
      {showDepositModal && (
        <div className="modal-overlay" onClick={() => setShowDepositModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Record Deposit Payment</h2>
              <button className="close-btn" onClick={() => setShowDepositModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="deposit-summary">
                <div className="summary-row">
                  <span>Deposit Amount</span>
                  <span className="amount">{formatCurrency(invoice.depositAmount)}</span>
                </div>
                {invoice.depositDueDate && (
                  <div className="summary-row">
                    <span>Deposit Due Date</span>
                    <span>{format(parseISO(invoice.depositDueDate), 'MMMM d, yyyy')}</span>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
                >
                  <option value="card">Credit/Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="venmo">Venmo</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <p className="deposit-note">
                Recording this deposit will mark {formatCurrency(invoice.depositAmount)} as paid. 
                The remaining balance will be {formatCurrency(invoice.total - (invoice.amountPaid || 0) - invoice.depositAmount)}.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDepositModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleRecordDeposit}>
                Record Deposit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Discount Modal */}
      {showDiscountModal && (
        <div className="modal-overlay" onClick={() => setShowDiscountModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Discount</h2>
              <button className="close-btn" onClick={() => setShowDiscountModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Discount Type</label>
                <div className="discount-type-options">
                  <button 
                    className={`discount-type-btn ${discountData.type === 'percent' ? 'active' : ''}`}
                    onClick={() => setDiscountData({...discountData, type: 'percent'})}
                  >
                    <Percent size={18} />
                    Percentage
                  </button>
                  <button 
                    className={`discount-type-btn ${discountData.type === 'fixed' ? 'active' : ''}`}
                    onClick={() => setDiscountData({...discountData, type: 'fixed'})}
                  >
                    <DollarSign size={18} />
                    Fixed Amount
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>{discountData.type === 'percent' ? 'Discount Percentage' : 'Discount Amount'}</label>
                <div className="discount-input">
                  {discountData.type === 'fixed' && <span className="prefix">$</span>}
                  <input
                    type="number"
                    min="0"
                    max={discountData.type === 'percent' ? 100 : invoice.subtotal}
                    value={discountData.value}
                    onChange={(e) => setDiscountData({...discountData, value: parseFloat(e.target.value) || 0})}
                  />
                  {discountData.type === 'percent' && <span className="suffix">%</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  placeholder="e.g., First-time client, Referral, etc."
                  value={discountData.reason}
                  onChange={(e) => setDiscountData({...discountData, reason: e.target.value})}
                />
              </div>
              
              <div className="discount-preview">
                <div className="preview-row">
                  <span>Current Subtotal</span>
                  <span>{formatCurrency(invoice.lineItems.filter(i => i.type !== 'discount').reduce((sum, i) => sum + i.total, 0))}</span>
                </div>
                <div className="preview-row discount">
                  <span>Discount</span>
                  <span>
                    -{formatCurrency(
                      discountData.type === 'percent'
                        ? invoice.lineItems.filter(i => i.type !== 'discount').reduce((sum, i) => sum + i.total, 0) * (discountData.value / 100)
                        : discountData.value
                    )}
                  </span>
                </div>
                <div className="preview-row total">
                  <span>New Total</span>
                  <span>{formatCurrency(
                    Math.max(0, 
                      invoice.lineItems.filter(i => i.type !== 'discount').reduce((sum, i) => sum + i.total, 0) - 
                      (discountData.type === 'percent'
                        ? invoice.lineItems.filter(i => i.type !== 'discount').reduce((sum, i) => sum + i.total, 0) * (discountData.value / 100)
                        : discountData.value)
                    )
                  )}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDiscountModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAddDiscount}
                disabled={discountData.value <= 0}
              >
                Apply Discount
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Late Fee Modal */}
      {showLateFeeModal && (
        <div className="modal-overlay" onClick={() => setShowLateFeeModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Late Fee</h2>
              <button className="close-btn" onClick={() => setShowLateFeeModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="late-fee-info">
                <div className="overdue-badge">
                  <AlertCircle size={18} />
                  <span>{getDaysOverdue(invoice)} days overdue</span>
                </div>
                <p className="overdue-note">
                  This invoice was due on {format(parseISO(invoice.dueDate), 'MMMM d, yyyy')}
                </p>
              </div>

              <div className="form-group">
                <label>Late Fee Type</label>
                <div className="discount-type-options">
                  <button 
                    className={`discount-type-btn ${lateFeeData.type === 'fixed' ? 'active' : ''}`}
                    onClick={() => setLateFeeData({...lateFeeData, type: 'fixed'})}
                  >
                    <DollarSign size={18} />
                    Fixed Amount
                  </button>
                  <button 
                    className={`discount-type-btn ${lateFeeData.type === 'percent' ? 'active' : ''}`}
                    onClick={() => setLateFeeData({...lateFeeData, type: 'percent'})}
                  >
                    <Percent size={18} />
                    Percentage
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>{lateFeeData.type === 'percent' ? 'Fee Percentage' : 'Fee Amount'}</label>
                <div className="discount-input">
                  {lateFeeData.type === 'fixed' && <span className="prefix">$</span>}
                  <input
                    type="number"
                    min="0"
                    value={lateFeeData.value}
                    onChange={(e) => setLateFeeData({...lateFeeData, value: parseFloat(e.target.value) || 0})}
                  />
                  {lateFeeData.type === 'percent' && <span className="suffix">%</span>}
                </div>
              </div>
              
              <div className="discount-preview">
                <div className="preview-row">
                  <span>Current Total</span>
                  <span>{formatCurrency(invoice.total)}</span>
                </div>
                <div className="preview-row late-fee">
                  <span>Late Fee</span>
                  <span className="fee-amount">
                    +{formatCurrency(
                      lateFeeData.type === 'percent'
                        ? invoice.total * (lateFeeData.value / 100)
                        : lateFeeData.value
                    )}
                  </span>
                </div>
                <div className="preview-row total">
                  <span>New Total</span>
                  <span>{formatCurrency(
                    invoice.total + (lateFeeData.type === 'percent'
                      ? invoice.total * (lateFeeData.value / 100)
                      : lateFeeData.value)
                  )}</span>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={lateFeeData.sendUpdatedInvoice}
                    onChange={(e) => setLateFeeData({...lateFeeData, sendUpdatedInvoice: e.target.checked})}
                  />
                  <span>Send updated invoice to client</span>
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowLateFeeModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-warning" 
                onClick={handleAddLateFee}
                disabled={saving || lateFeeData.value <= 0}
              >
                {saving ? (
                  <>
                    <Loader size={18} className="spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <AlertCircle size={18} />
                    Add Late Fee
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceDetail;
